import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Logo } from "@/components/app/Logo";
import { OutcomeForm } from "@/components/app/GrowthDialogs";
import { RATINGS } from "@/components/app/DecisionDialogs";
import { api, errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/ui";

export default function AllocationReview() {
  const { token } = useParams();
  const [a, setA] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    api.get(`/public/allocation-review/${token}`).then((r) => setA(r.data)).catch((e) => setError(errMsg(e)));
  }, [token]);

  const submit = async (c) => {
    setBusy(true);
    try {
      setA((await api.post(`/public/allocation-review/${token}`, c)).data);
      setSaved(true);
    } catch (e) {
      setError(errMsg(e));
    }
    setBusy(false);
  };
  const last = a?.outcomes?.[a.outcomes.length - 1];
  return (
    <div className="min-h-screen bg-[#0B0F19] text-slate-100 flex flex-col items-center px-4 py-10">
      <Link to="/" className="mb-8"><Logo invert /></Link>
      <div className="w-full max-w-lg rounded-2xl border border-white/10 bg-[#0E1424] p-6" data-testid="alloc-review-page">
        {!a && !error && <Loader2 className="h-5 w-5 animate-spin mx-auto" />}
        {error && !a && <p className="text-sm text-red-300" data-testid="alloc-review-error">{error}</p>}
        {a && <>
          <div className="text-xs uppercase tracking-wider text-emerald-400 font-semibold">Allocation review</div>
          <h1 className="mt-2 text-xl font-semibold" data-testid="alloc-review-title">{a.title}</h1>
          <p className="mt-1 text-sm text-slate-400">{a.resource === "money" ? `$${Number(a.amount).toLocaleString()}` : `${a.amount} ${a.unit}`} since {fmtDate(a.start_date)}</p>
          {a.forecast && <p className="mt-3 text-sm"><span className="text-slate-400">Forecast:</span> {a.forecast}{a.expected_value != null && ` (${a.expected_value} ${a.metric || ""})`}</p>}
          {a.assumptions && <p className="mt-1 text-sm"><span className="text-slate-400">Assumptions:</span> {a.assumptions}</p>}
          {saved ? (
            <div className="mt-5 rounded-xl border border-emerald-500/30 bg-emerald-500/[0.08] p-3.5 text-sm text-emerald-100" data-testid="alloc-review-saved">
              <div className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0 mt-0.5" />Saved and compared with your forecast.</div>
              {last?.variance && <p className="mt-2 text-xs">{last.variance}</p>}
              {last?.lessons && <p className="mt-1 text-xs"><b>Lesson:</b> {last.lessons}</p>}
            </div>) : <div className="mt-5"><OutcomeForm a={a} onSubmit={submit} busy={busy} dark /></div>}
          {error && <p className="mt-2 text-xs text-red-300">{error}</p>}
          {a.outcomes?.length > 0 && !saved && <ul className="mt-6 border-t border-white/10 pt-4 space-y-1 text-xs text-slate-400">{a.outcomes.slice().reverse().map((o) => <li key={o.at}>{fmtDate(o.at)} — <b className="text-slate-200">{RATINGS[o.rating][0]}</b>{o.actual_result && `: ${o.actual_result}`}</li>)}</ul>}
        </>}
      </div>
      <p className="mt-6 text-xs text-slate-500">AccruallQ · Corporate Health Operating System</p>
    </div>
  );
}
